import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Share2, Package, Lightbulb, Building2, CalendarHeart, Video, Play, X,
  ArrowRight, ChevronLeft, ChevronRight, Zap, Palette, Award, Gauge, Check, Star, Clapperboard, PenLine,
} from "lucide-react";
import { Reveal } from "./Reveal";
import { useCatalog, saleInfo, SaleBadge, money0 } from "./booking";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "./ui/accordion";

export const ICONS = {
  sparkles: Sparkles, "share-2": Share2, package: Package, lightbulb: Lightbulb,
  "building-2": Building2, "calendar-heart": CalendarHeart, video: Video,
  clapperboard: Clapperboard, "pen-line": PenLine,
};

export function SectionHeading({ eyebrow, title, subtitle, center, dark }) {
  return (
    <div className={`${center ? "text-center mx-auto" : ""} max-w-3xl`}>
      {eyebrow && (
        <span className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-brand-orange mb-3">
          {eyebrow}
        </span>
      )}
      <h2 className={`font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-[1.05] ${dark ? "text-white" : "text-brand-purpleDeep"}`}>
        {title}
      </h2>
      {subtitle && (
        <p className={`mt-4 text-base sm:text-lg leading-relaxed ${dark ? "text-white/70" : "text-brand-purpleDeep/60"}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

/* ---------------- Video Lightbox ---------------- */
export function VideoModal({ open, onClose, video }) {
  if (!video) return null;
  const isEmbed = video.video_url && (video.video_url.includes("youtube") || video.video_url.includes("vimeo"));
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-brand-purpleDeep/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="video-modal"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute -top-11 right-0 text-white/80 hover:text-white flex items-center gap-1.5 text-sm" data-testid="close-video-modal">
              Close <X size={20} />
            </button>
            <div className="aspect-video rounded-xl overflow-hidden bg-black shadow-2xl">
              {video.video_url ? (
                isEmbed ? (
                  <iframe src={video.video_url} className="w-full h-full" allow="autoplay; fullscreen" title={video.title} />
                ) : (
                  <video src={video.video_url} controls autoPlay className="w-full h-full" />
                )
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/50">Video coming soon</div>
              )}
            </div>
            <div className="mt-4 text-white">
              <span className="text-brand-orange text-xs font-bold uppercase tracking-wider">{video.category}</span>
              <h3 className="font-heading font-bold text-xl mt-1">{video.title}</h3>
              {video.description && <p className="text-white/60 text-sm mt-1">{video.description}</p>}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ---------------- Services ---------------- */
const SERVICE_MAP = [
  ["storyboard", "extra=Storyboard"], ["script", "extra=Scriptwriting"], ["brand", "type=Promotional Content"],
  ["social", "type=Social Media Content"], ["product", "type=Product Videos"], ["corporate", "type=Corporate Video"],
  ["event", "type=Event Coverage"], ["explainer", "type=Other"],
];
const serviceLink = (title = "") => {
  const hit = SERVICE_MAP.find(([k]) => title.toLowerCase().includes(k));
  if (!hit) return "/contact";
  const [key, val] = hit[1].split("=");
  return `/contact?${key}=${encodeURIComponent(val)}`;
};

export function ServicesSection({ services = [] }) {
  const navigate = useNavigate();
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionHeading
            eyebrow="What we do"
            title="Whatever you need to say, we'll help you show it."
            subtitle="From brand films to social content, we make the kind of video that actually gets watched."
          />
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => {
            const Icon = ICONS[s.icon] || Video;
            return (
              <Reveal key={s.id} delay={i * 0.06}>
                <div
                  className="group h-full bg-white rounded-2xl border border-border p-8 shadow-[0_4px_20px_rgba(130,26,200,0.05)] hover:shadow-[0_16px_50px_rgba(130,26,200,0.12)] hover:-translate-y-1 transition-[transform,box-shadow] duration-300 cursor-pointer"
                  onClick={() => navigate(serviceLink(s.title))}
                  data-testid={`service-card-${i}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-brand-light flex items-center justify-center text-brand-purple group-hover:bg-brand-purple group-hover:text-white transition-colors">
                    <Icon size={26} />
                  </div>
                  <h3 className="font-heading font-bold text-xl mt-6 text-brand-purpleDeep">{s.title}</h3>
                  <p className="mt-2.5 text-brand-purpleDeep/60 text-sm leading-relaxed">{s.description}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-orange opacity-0 group-hover:opacity-100 transition-opacity">
                    Get started <ArrowRight size={15} />
                  </span>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- How It Works ---------------- */
const STEPS = [
  { n: "01", title: "Choose your package", desc: "Pick a fixed price package that fits the content you need." },
  { n: "02", title: "Book your shoot date", desc: "Pick a date that works for you and lock it in online." },
  { n: "03", title: "Tell us about your project", desc: "Build your brief in your portal so we know exactly what to shoot." },
  { n: "04", title: "We shoot", desc: "Our crew turns up and captures everything on the day." },
  { n: "05", title: "Receive your videos", desc: "We edit and deliver your finished videos, with revisions included." },
];

export function HowItWorksSection() {
  return (
    <section className="py-24 sm:py-32 bg-[#FAFAFC]" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionHeading eyebrow="How it works" title="Making great video shouldn't be complicated." center />
        </Reveal>
        <div className="mt-16 relative">
          <div className="hidden lg:block absolute top-[34px] left-[34px] right-[calc((100%_-_8rem)/5_-_34px)] h-0.5 bg-gradient-to-r from-brand-purple/20 via-brand-orange/40 to-brand-purple/20" />
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5 lg:gap-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1} className={i === STEPS.length - 1 ? "sm:col-span-2 lg:col-span-1" : ""}>
                <div className="relative text-center lg:text-left" data-testid={`how-step-${s.n}`}>
                  <div className="mx-auto lg:mx-0 w-[68px] h-[68px] rounded-2xl bg-white border border-border shadow-[0_8px_30px_rgba(130,26,200,0.08)] flex items-center justify-center font-heading font-extrabold text-xl text-brand-purple relative z-10">
                    {s.n}
                  </div>
                  <h3 className="font-heading font-bold text-lg mt-5 text-brand-purpleDeep">{s.title}</h3>
                  <p className="mt-2 text-sm text-brand-purpleDeep/60 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Portfolio ---------------- */
export function PortfolioSection({ projects = [], limit }) {
  const [active, setActive] = useState(null);
  const list = limit ? projects.slice(0, limit) : projects;
  const navigate = useNavigate();
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <SectionHeading eyebrow="Our work" title="Good videos speak for themselves." />
            {limit && (
              <button onClick={() => navigate("/work")} className="self-start sm:self-auto inline-flex items-center gap-2 text-brand-purple font-semibold hover:gap-3 transition-all" data-testid="view-all-work">
                View all work <ArrowRight size={17} />
              </button>
            )}
          </div>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08}>
              <div
                className="group relative rounded-2xl overflow-hidden cursor-pointer border border-border bg-brand-purpleDeep"
                onClick={() => setActive(p)}
                data-testid={`portfolio-item-${i}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={p.thumbnail_url}
                    alt={p.title}
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-[transform,opacity] duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-brand-purpleDeep/85 via-brand-purpleDeep/10 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-brand-orange/95 flex items-center justify-center text-white scale-90 group-hover:scale-100 opacity-80 group-hover:opacity-100 transition-all shadow-lg">
                    <Play size={22} className="ml-0.5" fill="currentColor" />
                  </div>
                </div>
                <div className="absolute bottom-0 inset-x-0 p-5">
                  <span className="text-brand-orange text-[11px] font-bold uppercase tracking-wider">{p.category}</span>
                  <h3 className="text-white font-heading font-bold text-lg leading-tight">{p.title}</h3>
                  <p className="text-white/60 text-xs mt-0.5">{p.client}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <VideoModal open={!!active} onClose={() => setActive(null)} video={active} />
    </section>
  );
}

/* ---------------- Why EasyVideo ---------------- */
const WHY = [
  { icon: Zap, title: "Simple", desc: "No confusing production process." },
  { icon: Palette, title: "Creative", desc: "Ideas that actually make your business stand out." },
  { icon: Award, title: "Professional", desc: "High-quality filming, editing and production." },
  { icon: Gauge, title: "Fast", desc: "A streamlined process designed to keep projects moving." },
];

export function WhySection() {
  return (
    <section className="py-24 sm:py-32 bg-[#FAFAFC]" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionHeading eyebrow="Why EasyVideo" title="Professional video. Without the production headache." center />
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08}>
              <div className="h-full bg-white rounded-2xl border border-border p-8 text-center hover:-translate-y-1 transition-transform duration-300">
                <div className="mx-auto w-16 h-16 rounded-2xl bg-brand-orangeLight flex items-center justify-center text-brand-orange">
                  <w.icon size={28} />
                </div>
                <h3 className="font-heading font-bold text-xl mt-5 text-brand-purpleDeep">{w.title}</h3>
                <p className="mt-2 text-sm text-brand-purpleDeep/60">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Pricing ---------------- */
export function PricingSection({ packages = [], addons = [], settings = {}, hideAddons = false }) {
  const navigate = useNavigate();
  const visible = packages.filter((p) => p.visible !== false);
  const cat = useCatalog();
  const catPkg = (p) => cat.items.find((i) => i.category === "package" && i.name === p.name);
  const go = (pkg) => navigate(`/contact?package=${encodeURIComponent(pkg.name)}`);
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionHeading
            eyebrow={settings.pricing_headline || "Fixed-price packages"}
            title="Pick a package. Build your quote."
            subtitle={settings.pricing_subtitle || "Transparent pricing, no surprises. Pick a package and we'll help you build the right quote."}
            center
          />
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 items-stretch">
          {visible.map((p, i) => { const sale = catPkg(p) ? saleInfo(catPkg(p), cat) : null; const dark = p.popular && !sale; return (
            <Reveal key={p.id} delay={i * 0.06}>
              <div
                className={`relative h-full rounded-2xl p-7 transition-transform duration-300 hover:-translate-y-1 flex flex-col ${
                  sale ? "bg-brand-orangeLight/50 border-2 border-brand-orange shadow-[0_16px_40px_rgba(255,101,0,0.18)]" : dark ? "border bg-brand-purpleDeep border-brand-purple text-white shadow-2xl lg:scale-[1.03]" : "border bg-white border-border"
                }`}
                data-testid={`pricing-card-${i}`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-orange text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-lg whitespace-nowrap" data-testid="most-popular-badge">
                    ★ Most Popular
                  </span>
                )}
                {sale && <div className="mb-3"><SaleBadge s={sale} testid={`pricing-card-sale-badge-${i}`} /></div>}
                <h3 className={`font-heading font-extrabold text-xl ${dark ? "text-white" : "text-brand-purpleDeep"}`}>{p.name}</h3>
                {sale ? <div className="mt-2" data-testid={`pricing-card-price-${i}`}><div className="text-sm text-brand-purpleDeep/50">was <s data-testid={`pricing-card-was-${i}`}>{money0(sale.was)}</s></div><div className="font-heading font-extrabold text-3xl text-brand-orange" data-testid={`pricing-card-sale-price-${i}`}>{money0(sale.price)}</div></div> : <div className={`mt-2 font-heading font-extrabold text-3xl ${dark ? "text-brand-orange" : "text-brand-purple"}`}>{p.price}</div>}
                <p className={`mt-2 text-sm ${dark ? "text-white/70" : "text-brand-purpleDeep/60"}`}>{p.tagline}</p>
                <div className={`mt-5 pb-4 border-b space-y-1 ${dark ? "border-white/15" : "border-border"}`} data-testid={`pricing-card-scope-${i}`}>
                  {p.shoot_hours && <div className={`font-heading font-bold text-sm ${dark ? "text-white" : "text-brand-purpleDeep"}`}>{p.shoot_hours}</div>}
                  {p.videos && <div className={`font-heading font-bold text-sm ${dark ? "text-white" : "text-brand-purpleDeep"}`}>{p.videos}</div>}
                </div>
                <ul className="mt-4 space-y-2.5 flex-1">
                  {(p.features || []).filter((f) => f !== p.shoot_hours && f !== p.videos).map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm">
                      <Check size={16} className={`mt-0.5 shrink-0 ${dark ? "text-brand-orange" : "text-brand-purple"}`} />
                      <span className={dark ? "text-white/85" : "text-brand-purpleDeep/70"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => go(p)}
                  className={`mt-7 w-full font-semibold py-3 rounded-lg transition-all hover:-translate-y-0.5 ${
                    dark ? "bg-brand-orange hover:bg-brand-orangeDark text-white shadow-[0_10px_30px_rgba(255,101,0,0.3)]" : "bg-brand-purple hover:bg-brand-purpleDark text-white"
                  }`}
                  data-testid={`pricing-cta-${i}`}
                >
                  {p.cta || "Select Package"}
                </button>
              </div>
            </Reveal>
          ); })}
        </div>

        {visible.length > 0 && (
          <p className="mt-8 text-center text-sm text-brand-purpleDeep/60" data-testid="pricing-extras-note">
            Add extras like advanced editing, extra revisions, lighting, scripting and talent when you build your quote.
          </p>
        )}

        {!hideAddons && addons.length > 0 && (
          <div className="mt-20">
            <Reveal>
              <SectionHeading eyebrow="Optional upgrades" title="Make your shoot even better." subtitle="Add audio, lighting or extra editing during your quote — only pay for what you need." center />
            </Reveal>
            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {addons.map((a, i) => (
                <Reveal key={a.id} delay={(i % 3) * 0.05}>
                  <div className="h-full bg-[#FAFAFC] rounded-xl border border-border p-5 hover:border-brand-orange/40 transition-colors" data-testid={`addon-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="font-heading font-bold text-brand-purpleDeep">{a.name}</h4>
                      <span className="text-brand-orange font-heading font-extrabold text-sm whitespace-nowrap">{a.price_label}</span>
                    </div>
                    <p className="mt-2 text-sm text-brand-purpleDeep/60">{a.description}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ---------------- Testimonials ---------------- */
export function TestimonialsSection({ testimonials = [] }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (testimonials.length <= 1) return;
    const t = setInterval(() => setIdx((v) => (v + 1) % testimonials.length), 6000);
    return () => clearInterval(t);
  }, [testimonials.length]);
  if (!testimonials.length) return null;
  const t = testimonials[idx];
  return (
    <section className="py-24 sm:py-32 bg-[#FAFAFC]" data-testid="testimonials-section">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
        <Reveal>
          <span className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-brand-orange mb-6">What clients say</span>
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex justify-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => <Star key={i} size={20} className="text-brand-orange" fill="currentColor" />)}
              </div>
              <blockquote className="font-heading font-bold text-2xl sm:text-3xl leading-snug text-brand-purpleDeep">
                "{t.quote}"
              </blockquote>
              <div className="mt-8 flex items-center justify-center gap-3">
                {t.avatar_url && <img src={t.avatar_url} alt={t.name} className="w-12 h-12 rounded-full object-cover" />}
                <div className="text-left">
                  <div className="font-semibold text-brand-purpleDeep">{t.name}</div>
                  <div className="text-sm text-brand-purpleDeep/60">{t.position}{t.company ? `, ${t.company}` : ""}</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </Reveal>
        {testimonials.length > 1 && (
          <div className="mt-10 flex items-center justify-center gap-3">
            <button onClick={() => setIdx((v) => (v - 1 + testimonials.length) % testimonials.length)} className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-brand-purple hover:bg-brand-purple hover:text-white transition-colors" data-testid="testimonial-prev">
              <ChevronLeft size={18} />
            </button>
            {testimonials.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-brand-orange" : "w-2 bg-brand-purple/25"}`} />
            ))}
            <button onClick={() => setIdx((v) => (v + 1) % testimonials.length)} className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-brand-purple hover:bg-brand-purple hover:text-white transition-colors" data-testid="testimonial-next">
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
export function FaqSection({ faqs = [] }) {
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionHeading eyebrow="FAQ" title="Questions, answered." center />
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-12 space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={f.id} value={`item-${i}`} className="border border-border rounded-xl px-5 data-[state=open]:border-brand-purple/40 data-[state=open]:bg-brand-light/40 transition-colors">
                <AccordionTrigger className="text-left font-heading font-semibold text-brand-purpleDeep hover:no-underline py-5" data-testid={`faq-trigger-${i}`}>
                  {f.question}
                </AccordionTrigger>
                <AccordionContent className="text-brand-purpleDeep/70 leading-relaxed pb-5">
                  {f.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Final CTA ---------------- */
export function FinalCta({ settings = {} }) {
  const navigate = useNavigate();
  return (
    <section className="py-20 px-5 sm:px-8" data-testid="final-cta">
      <div className="max-w-6xl mx-auto rounded-3xl bg-brand-purple relative overflow-hidden px-8 py-20 sm:py-24 text-center">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-brand-orange/30 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
        <div className="relative">
          <h2 className="font-heading font-extrabold text-3xl sm:text-5xl text-white leading-tight">{settings.final_cta_title || "Ready to create your first video?"}</h2>
          <p className="mt-4 text-white/80 max-w-xl mx-auto text-lg">{settings.final_cta_subtitle || "Packages start at $999. Tell us what you're looking to create and we'll take it from there."}</p>
          <button
            onClick={() => navigate("/contact")}
            className="mt-8 inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeDark text-white font-semibold px-8 py-4 rounded-lg transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(255,101,0,0.35)]"
            data-testid="final-cta-button"
          >
            Start Your Video From $999 <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
